import { useRequest } from "@certn/api";
import { useAPI } from "@certn/api/src/useAPI";
import { useTimeTracking } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate, useParams } from "react-router-dom";
import { JsonFormsCore } from "@jsonforms/core";
import { isApplicationSchema, getAllSectionKeys } from "@certn/application";
import { useState, useEffect } from "react";

interface QuickscreenCaseData {
  data_schema: Record<string, unknown>;
  ui_schema: Record<string, unknown>;
  applicant_form_data: Record<string, unknown>;
  section_statuses: Record<string, unknown>;
  customer_name?: string;
  client_group_name?: string;
  client_group_address?: Record<string, unknown>;
  co_branding?: Record<string, unknown>;
}

export function useQuickscreenApplicationLogic() {
  const navigate = useNavigate();
  const { enableFpCoBrandingV2 } = useFlags();
  const { caseId, sectionName: routeSectionName } = useParams();
  const { data, mutate } = useAPI<QuickscreenCaseData>(
    `/api/internal/client-portal/quickscreen-case/${caseId}/`
  );
  const request = useRequest();
  const { stopTimeTracker } = useTimeTracking();

  // Track if the application has started (data loaded)
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (data) setStarted(true);
  }, [data]);

  // Validate and normalize routeSectionName before using it
  const validatedSectionName = (() => {
    if (!started || !data || !routeSectionName) return routeSectionName;

    // Special sections that are always valid
    if (routeSectionName === "summary") {
      return routeSectionName;
    }
    // Check if section exists in UI schema
    if (isApplicationSchema(data.ui_schema)) {
      const validSections = getAllSectionKeys(data.ui_schema);
      if (validSections.includes(routeSectionName)) {
        return routeSectionName;
      }
    }

    // If invalid section, don't pass it to Application component
    // Application will handle the redirect internally
    return undefined;
  })();

  // Redirect to /quickscreen/:caseId/summary if sectionName is missing and started
  useEffect(() => {
    if (started && !routeSectionName && caseId) {
      navigate(`/quickscreen/${caseId}/summary`, { replace: true });
    }
  }, [started, routeSectionName, caseId, navigate]);

  const handleSave = async (values: JsonFormsCore["data"], validate = true) => {
    // Summary is a special section that doesn't have form data to save
    if (routeSectionName === "summary") {
      return;
    }

    await request(
      `/api/internal/client-portal/quickscreen-case/${caseId}/sections/${routeSectionName}/?validate=${validate}`,
      {
        method: "POST",
        body: JSON.stringify(values),
      }
    );
    const response = await mutate();
    return response?.applicant_form_data;
  };

  const handleSubmit = async () => {
    await request(
      `/api/internal/client-portal/quickscreen-case/${caseId}/submit/`,
      {
        method: "POST",
        body: JSON.stringify(data?.applicant_form_data),
      }
    );
    stopTimeTracker("application--submit");
    navigate("/cases");
  };

  return {
    data,
    mutate,
    request,
    stopTimeTracker,
    handleSave,
    handleSubmit,
    enableFpCoBrandingV2,
    caseId,
    routeSectionName,
    validatedSectionName,
    started,
  };
}
